/**
 * One dot per asking of one question.
 *
 * "Gemini not named · GPT named" collapses a whole history into two words per
 * model, so a question named in four runs out of four and one named only in the
 * most recent run read identically. A row of dots says how long something has
 * been true, which is the thing you act on: one green at the end is a win to
 * confirm, four greens is a position to defend, and green turning red is the
 * reason to look at what changed that week.
 *
 * Three colours, not two. Amber is `cited_only`, which means the AI read one of
 * your pages and answered without naming you. Painting that red would throw away
 * the exact distinction the citation fix bought, and it is a different job:
 * absent means earn a mention, cited means claim the credit you already earned.
 */
export const DOT_STATE: Record<string, { c: string; t: string }> = {
  mentioned: { c: 'bg-success', t: 'named in the answer' },
  cited_only: { c: 'bg-estimated', t: 'your page was read, but you were not named' },
  absent: { c: 'bg-[#E0574A]/70', t: 'not named, not cited' },
};

export const engineName = (e: string): string => (e === 'gemini' ? 'Gemini' : 'GPT');

export default function StateDots({ points }: {
  points: { at: string; engine: string; state: string }[];
}): JSX.Element {
  return (
    <span className="flex flex-wrap gap-1 items-center">
      {points.map((p, i) => {
        const s = DOT_STATE[p.state] ?? { c: 'bg-warm-dark-2', t: p.state };
        return (
          <span
            key={i}
            // Both, because a tooltip is not reachable by everyone and a screen
            // reader gets nothing from a coloured span.
            title={`${new Date(p.at).toLocaleString()} · ${engineName(p.engine)} · ${s.t}`}
            aria-label={`${new Date(p.at).toLocaleDateString()}, ${engineName(p.engine)}, ${s.t}`}
            role="img"
            className={`inline-block w-2.5 h-2.5 rounded-full ${s.c} ${
              p.engine === 'openai' ? 'ring-1 ring-text-secondary/50' : ''
            }`}
          />
        );
      })}
    </span>
  );
}
